import React, { useState, useMemo, useCallback, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useSearchParams } from 'react-router-dom'
import { useAskQuestionData, useDeleteAskQuestion, useCreateAskQuestion, useUpdateAskQuestion } from 'hooks/askQuestion'
import { useNotificationBar } from 'context/notificationContext'
import { Question } from './types'
import { getImageSrc, getUpdatedSearchParams, parseSearchParams } from 'services/util'
import { CustomTable } from 'components/customTable'
import { ColumnDef, RowSelectionState } from '@tanstack/react-table'
import { LoaderMain } from 'components/loader'
import { RenderDefaultActions } from '../../common'
import { SmoothTransition } from 'components/pageTransition'
import { ConfirmModal } from 'components/confirmationPopup'
import FormModal from './FormModal'

const AskQuestionSettings = () => {
    const notification = useNotificationBar()
    const [searchParams, setSearchParams] = useSearchParams()
    const filterParams: Record<string, any> = parseSearchParams(searchParams, 'filter')

    // Fetch data
    const { data: questionsData, isLoading, refetch } = useAskQuestionData(filterParams, {
        keepPreviousData: true,
    })

    const createQuestion = useCreateAskQuestion()
    const updateQuestion = useUpdateAskQuestion()
    const deleteQuestion = useDeleteAskQuestion()

    const filterFormHook = useForm<any>({
        defaultValues: {
            pagination: filterParams?.pagination || { pageIndex: 0, pageSize: 10 },
        },
    })
    const rowSelectionState = useState<RowSelectionState>({})
    const [showModal, setShowModal] = useState(false)
    const [editingQuestion, setEditingQuestion] = useState<Question | null>(null)
    const [parentForNewQuestion, setParentForNewQuestion] = useState<Question | null>(null)
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
    const [questionToDelete, setQuestionToDelete] = useState<Question | null>(null)

    useEffect(() => {
        const subscriber = filterFormHook.watch((values, { name }) => {
            if (!name) return

            const updatedParams = getUpdatedSearchParams(filterParams, values, name)
            if (Object.keys(updatedParams).length) {
                setSearchParams({ filter: encodeURIComponent(JSON.stringify(updatedParams)) })
            } else {
                setSearchParams({})
            }
        })

        return () => subscriber.unsubscribe()
    }, [filterFormHook, filterParams, setSearchParams])

    const handleAddNew = useCallback(() => {
        setEditingQuestion(null)
        setParentForNewQuestion(null)
        setShowModal(true)
    }, [])

    const handleAddChild = useCallback((parent: Question) => {
        setEditingQuestion(null)
        setParentForNewQuestion(parent)
        setShowModal(true)
    }, [])

    const handleEdit = useCallback((questionData: Question) => {
        setEditingQuestion(questionData)
        setParentForNewQuestion(null)
        setShowModal(true)
    }, [])

    // Extract all questions from API response
    const allQuestions: Question[] = useMemo(() => {
        if (!questionsData?.data?.results) return []
        return Array.isArray(questionsData.data.results) ? questionsData.data.results : []
    }, [questionsData])

    const handleDeleteClick = useCallback((question: Question) => {
        // Check if Level-1 has children
        if (question?.level === 'Level-1') {
            const hasChildren = allQuestions.some(q => q.parent_option === question.id)
            if (hasChildren) {
                notification.push({
                    title: 'Error',
                    type: 'error',
                    message: 'Cannot delete a question that has children. Please delete all child questions first.',
                })
                return
            }
        }
        setQuestionToDelete(question)
        setDeleteConfirmOpen(true)
    }, [allQuestions, notification])

    const handleDeleteConfirm = useCallback(async () => {
        if (!questionToDelete) return

        try {
            await deleteQuestion.mutateAsync({
                questionId: questionToDelete.id
            })
            notification.push({
                title: 'Success',
                type: 'success',
                message: 'Question deleted successfully'
            })
            refetch()
        } catch (error: any) {
            notification.push({
                title: 'Error',
                type: 'error',
                message: error?.response?.data?.message || 'Failed to delete question'
            })
        } finally {
            setDeleteConfirmOpen(false)
            setQuestionToDelete(null)
        }
    }, [questionToDelete, deleteQuestion, notification, refetch])

    const onAction = useCallback((actionType: string, rowData: any) => {
        if (actionType === 'edit') handleEdit(rowData)
        if (actionType === 'delete') handleDeleteClick(rowData)
        if (actionType === 'add_child') handleAddChild(rowData)
    }, [handleEdit, handleDeleteClick, handleAddChild])

    // Separate Level-1 and Level-2 questions
    const level1Questions = useMemo(() => allQuestions.filter(q => q.level === 'Level-1'), [allQuestions])
    const level2QuestionsByParent = useMemo(() => {
        return allQuestions
            .filter(q => q.level === 'Level-2')
            .reduce((acc, q) => {
                if (q.parent_option) {
                    if (!acc[q.parent_option]) acc[q.parent_option] = []
                    acc[q.parent_option].push(q)
                }
                return acc
            }, {} as Record<number, Question[]>)
    }, [allQuestions])

    const columns = useMemo<ColumnDef<Question>[]>(
        () => [
            {
                id: 'sr_no',
                header: 'ID',
                cell: ({ row, table }) => {
                    const pageIndex = table.getState().pagination.pageIndex
                    const pageSize = table.getState().pagination.pageSize
                    return pageIndex * pageSize + row.index + 1
                },
                enableSorting: false,
            },
            {
                accessorKey: 'option_title',
                header: 'QUESTION TITLE',
                cell: ({ row }) => (
                    <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {row.original.option_title}
                        </span>
                        {row.original.level === 'Level-1' && level2QuestionsByParent[row.original.id]?.length > 0 && (
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                ({level2QuestionsByParent[row.original.id].length} children)
                            </span>
                        )}
                    </div>
                ),
            },
            {
                accessorKey: 'level',
                header: 'LEVEL',
                cell: ({ row }) => (
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                        row.original.level === 'Level-1'
                            ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                            : 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
                    }`}>
                        {row.original.level}
                    </span>
                ),
            },
            {
                accessorKey: 'chip_image',
                header: 'ICON',
                cell: ({ row }) => {
                    const chipImage = row.original.chip_image
                    if (!chipImage) return <span className="text-gray-400">-</span>
                    
                    return (
                        <img
                            src={chipImage.startsWith('http') ? chipImage : getImageSrc(chipImage)}
                            alt="Icon"
                            className="w-8 h-8 object-contain"
                        />
                    )
                },
            },
            {
                id: 'status',
                header: 'STATUS',
                accessorFn: (row) => row.status,
                cell: ({ row }) => {
                    const status = row.original.status
                    return (
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            status === 'active'
                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                        }`}>
                            {status === 'active' ? 'Active' : 'Inactive'}
                        </span>
                    )
                },
            },
            {
                id: 'actions',
                header: 'ACTIONS',
                cell: ({ row }) => {
                    const actions = row.original.level === 'Level-1' 
                        ? ['edit', 'delete'] as any
                        : ['edit', 'delete'] as any
                    return RenderDefaultActions(row, actions, onAction)
                },
            },
        ],
        [onAction, level2QuestionsByParent]
    )

    const responseData = useMemo(() => {
        if (!questionsData?.data) {
            return {
                results: [],
                pages: 1,
                total: 0,
                pagination: { pageSize: 10, pageIndex: 0 }
            }
        }

        const data = questionsData.data
        return {
            ...data,
            results: allQuestions, // Show all questions (Level-1 and Level-2)
        }
    }, [questionsData, allQuestions])

    if (isLoading) {
        return <LoaderMain />
    }

    return (
        <SmoothTransition>
            <div className="w-full px-6 pt-2 bg-white dark:bg-slate-800 rounded-lg shadow">
                <CustomTable
                    columns={columns}
                    data={responseData.results}
                    pages={responseData.pages || 1}
                    totalRecords={responseData.total || allQuestions.length}
                    rowSelectionState={rowSelectionState}
                    isLoading={isLoading}
                    formHook={filterFormHook}
                    title="Onboarding Questions"
                    onAddAction={handleAddNew}
                    addButtonText="Create Question"
                    onRefresh={refetch}
                    searchVisible={false}
                />

                {/* Add/Edit Modal */}
                {showModal && (
                    <FormModal
                        isOpen={showModal}
                        onClose={() => {
                            setShowModal(false)
                            setEditingQuestion(null)
                            setParentForNewQuestion(null)
                        }}
                        editingQuestion={editingQuestion}
                        parentQuestion={parentForNewQuestion}
                        level1Questions={level1Questions}
                        onSuccess={() => {
                            refetch()
                            setShowModal(false)
                            setEditingQuestion(null)
                            setParentForNewQuestion(null)
                        }}
                        createQuestion={createQuestion}
                        updateQuestion={updateQuestion}
                        notification={notification}
                    />
                )}

                {/* Delete Confirmation Modal */}
                <ConfirmModal
                    open={deleteConfirmOpen}
                    title="Delete Question"
                    message={`Are you sure you want to delete "${questionToDelete?.option_title}"? This action cannot be undone.`}
                    confirmText="Delete"
                    cancelText="Cancel"
                    variant="danger"
                    onCancel={() => {
                        setDeleteConfirmOpen(false)
                        setQuestionToDelete(null)
                    }}
                    onConfirm={handleDeleteConfirm}
                />
            </div>
        </SmoothTransition>
    )
}

export default AskQuestionSettings
